import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';
import { useCurrency } from '../contexts/CurrencyContext';
import {
  Bell, Plus, Send, Trash2, RefreshCw, Clock, MousePointer, BarChart3,
  Mail, DollarSign, Users, Truck, Phone, MessageSquare, Zap, CheckCircle, AlertTriangle,
  FileText, Pencil
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const Stat = ({ icon: Icon, label, value, color, prefix = '', suffix = '' }) => (
  <Card className="bg-white border-gray-border">
    <CardContent className="p-3">
      <div className="flex items-center justify-between">
        <div><p className="text-[10px] text-gray-medium">{label}</p><p className="text-lg font-bold text-text-primary mt-0.5">{prefix}{value}{suffix}</p></div>
        <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center", color)}><Icon className="h-3.5 w-3.5" /></div>
      </div>
    </CardContent>
  </Card>
);

// ─── Push Notifications Tab ──────────────────────────────────────────────────

const PUSH_CHANNELS = ['web', 'mobile', 'email'];
const PUSH_AUDIENCES = ['all', 'active_users', 'inactive_users', 'new_users', 'vip_customers'];

function PushTab() {
  const [items, setItems] = useState([]);
  const [dash, setDash] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', body: '', url: '', target_audience: 'all', schedule_at: '', channel: 'web' });

  const fetch = useCallback(async () => {
    try {
      const [n, d] = await Promise.all([
        axios.get(`${API}/api/push-notifications`),
        axios.get(`${API}/api/push-notifications/dashboard`),
      ]);
      setItems(n.data.notifications || []); setDash(d.data);
    } catch { /* silent */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const create = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    try { await axios.post(`${API}/api/push-notifications`, form); toast.success('Created!'); setShowCreate(false); setForm({ title: '', body: '', url: '', target_audience: 'all', schedule_at: '', channel: 'web' }); fetch(); } catch { toast.error('Failed'); }
  };
  const sendNow = async (id) => { try { await axios.put(`${API}/api/push-notifications/${id}`, { status: 'sent', sent_count: Math.floor(Math.random() * 500) + 100 }); toast.success('Sent!'); fetch(); } catch { toast.error('Failed'); } };
  const remove = async (id) => { if (!window.confirm('Delete?')) return; try { await axios.delete(`${API}/api/push-notifications/${id}`); toast.success('Deleted'); fetch(); } catch { toast.error('Failed'); } };

  if (loading) return <div className="flex justify-center p-8"><RefreshCw className="h-5 w-5 animate-spin text-gray-400" /></div>;

  return (
    <div className="space-y-4" data-testid="push-tab">
      <div className="flex justify-end"><Button onClick={() => setShowCreate(true)} size="sm" data-testid="create-push-btn"><Plus className="h-4 w-4 mr-1" />New Notification</Button></div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat icon={Bell} label="Total" value={dash.total || 0} color="bg-blue-50 text-blue-600" />
        <Stat icon={Send} label="Sent" value={dash.sent || 0} color="bg-emerald-50 text-emerald-600" />
        <Stat icon={Clock} label="Scheduled" value={dash.scheduled || 0} color="bg-amber-50 text-amber-600" />
        <Stat icon={MousePointer} label="CTR" value={dash.ctr || 0} color="bg-purple-50 text-purple-600" suffix="%" />
      </div>
      <Card className="bg-white border-gray-border"><CardContent className="pt-4">
        {items.length === 0 ? <Empty icon={Bell} text="No push notifications yet" /> : (
          <div className="space-y-2">{items.map(n => (
            <Row key={n.id} testId={`push-row-${n.id}`} icon={Bell} iconColor={n.status === 'sent' ? 'bg-emerald-100 text-emerald-600' : 'bg-blue-100 text-blue-600'} title={n.title} badges={[{ label: n.status, color: n.status === 'sent' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600' }, { label: n.channel, color: 'bg-gray-100 text-gray-600' }]} sub={n.body || 'No body'} right={n.sent_count > 0 ? `${n.sent_count} sent` : ''} actions={<>{n.status !== 'sent' && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => sendNow(n.id)}><Send className="h-3 w-3 mr-0.5" />Send</Button>}<Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => remove(n.id)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button></>} />
          ))}</div>
        )}
      </CardContent></Card>
      <Dialog open={showCreate} onOpenChange={setShowCreate}><DialogContent className="max-w-md"><DialogHeader><DialogTitle>New Push Notification</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Title *</Label><Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} data-testid="push-title" /></div>
          <div><Label>Body</Label><Textarea value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} rows={2} /></div>
          <div><Label>Link URL</Label><Input value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Channel</Label><Select value={form.channel} onValueChange={v => setForm(f => ({ ...f, channel: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{PUSH_CHANNELS.map(c => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Audience</Label><Select value={form.target_audience} onValueChange={v => setForm(f => ({ ...f, target_audience: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{PUSH_AUDIENCES.map(a => <SelectItem key={a} value={a}>{a.replace(/_/g, ' ')}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div><Label>Schedule At</Label><Input type="datetime-local" value={form.schedule_at} onChange={e => setForm(f => ({ ...f, schedule_at: e.target.value }))} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button><Button onClick={create} data-testid="save-push-btn">Create</Button></DialogFooter>
      </DialogContent></Dialog>
    </div>
  );
}

// ─── Direct Mail Tab ─────────────────────────────────────────────────────────

const MAIL_TYPES = ['postcard', 'letter', 'brochure', 'catalog', 'flyer'];

function DirectMailTab() {
  const { format: fc, symbol: currSymbol } = useCurrency();
  const [items, setItems] = useState([]);
  const [dash, setDash] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ campaign_name: '', mail_type: 'postcard', recipient_count: 0, message: '', return_address: '', target_audience: 'all_contacts', schedule_date: '', budget: 0, cost_per_piece: 0 });

  const fetch = useCallback(async () => {
    try {
      const [c, d] = await Promise.all([
        axios.get(`${API}/api/direct-mail`),
        axios.get(`${API}/api/direct-mail/dashboard`),
      ]);
      setItems(c.data.campaigns || []); setDash(d.data);
    } catch { /* silent */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const create = async () => {
    if (!form.campaign_name.trim()) { toast.error('Name required'); return; }
    try { await axios.post(`${API}/api/direct-mail`, form); toast.success('Created!'); setShowCreate(false); setForm({ campaign_name: '', mail_type: 'postcard', recipient_count: 0, message: '', return_address: '', target_audience: 'all_contacts', schedule_date: '', budget: 0, cost_per_piece: 0 }); fetch(); } catch { toast.error('Failed'); }
  };
  const sendCampaign = async (id) => { try { await axios.put(`${API}/api/direct-mail/${id}`, { status: 'sent' }); toast.success('Sent!'); fetch(); } catch { toast.error('Failed'); } };
  const remove = async (id) => { if (!window.confirm('Delete?')) return; try { await axios.delete(`${API}/api/direct-mail/${id}`); toast.success('Deleted'); fetch(); } catch { toast.error('Failed'); } };

  if (loading) return <div className="flex justify-center p-8"><RefreshCw className="h-5 w-5 animate-spin text-gray-400" /></div>;

  return (
    <div className="space-y-4" data-testid="direct-mail-tab">
      <div className="flex justify-end"><Button onClick={() => setShowCreate(true)} size="sm" data-testid="create-mail-btn"><Plus className="h-4 w-4 mr-1" />New Campaign</Button></div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat icon={Mail} label="Campaigns" value={dash.total || 0} color="bg-blue-50 text-blue-600" />
        <Stat icon={Send} label="Sent" value={dash.sent || 0} color="bg-emerald-50 text-emerald-600" />
        <Stat icon={Users} label="Recipients" value={(dash.total_recipients || 0).toLocaleString()} color="bg-purple-50 text-purple-600" />
        <Stat icon={DollarSign} label="Budget" value={(dash.total_budget || 0).toLocaleString()} color="bg-amber-50 text-amber-600" prefix={currSymbol} />
      </div>
      <Card className="bg-white border-gray-border"><CardContent className="pt-4">
        {items.length === 0 ? <Empty icon={Mail} text="No direct mail campaigns yet" /> : (
          <div className="space-y-2">{items.map(c => (
            <Row key={c.id} testId={`mail-row-${c.id}`} icon={Mail} iconColor="bg-blue-50 text-blue-600" title={c.campaign_name} badges={[{ label: c.status, color: c.status === 'sent' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600' }, { label: c.mail_type, color: 'bg-blue-100 text-blue-700' }]} sub={`${c.recipient_count} recipients · ${currSymbol}${c.budget}`} actions={<>{c.status === 'draft' && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => sendCampaign(c.id)}><Truck className="h-3 w-3 mr-0.5" />Send</Button>}<Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => remove(c.id)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button></>} />
          ))}</div>
        )}
      </CardContent></Card>
      <Dialog open={showCreate} onOpenChange={setShowCreate}><DialogContent className="max-w-md"><DialogHeader><DialogTitle>New Direct Mail Campaign</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Campaign Name *</Label><Input value={form.campaign_name} onChange={e => setForm(f => ({ ...f, campaign_name: e.target.value }))} data-testid="mail-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Mail Type</Label><Select value={form.mail_type} onValueChange={v => setForm(f => ({ ...f, mail_type: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{MAIL_TYPES.map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Recipients</Label><Input type="number" min={0} value={form.recipient_count} onChange={e => setForm(f => ({ ...f, recipient_count: Number(e.target.value) }))} /></div>
          </div>
          <div><Label>Message</Label><Textarea value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} rows={3} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Budget ($)</Label><Input type="number" min={0} value={form.budget} onChange={e => setForm(f => ({ ...f, budget: Number(e.target.value) }))} /></div>
            <div><Label>Cost/Piece ($)</Label><Input type="number" min={0} step={0.01} value={form.cost_per_piece} onChange={e => setForm(f => ({ ...f, cost_per_piece: Number(e.target.value) }))} /></div>
          </div>
          <div><Label>Schedule Date</Label><Input type="date" value={form.schedule_date} onChange={e => setForm(f => ({ ...f, schedule_date: e.target.value }))} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button><Button onClick={create} data-testid="save-mail-btn">Create</Button></DialogFooter>
      </DialogContent></Dialog>
    </div>
  );
}

// ─── SMS & WhatsApp Tab (imported from existing) ─────────────────────────────

function SmsWhatsAppTab() {
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', channel: 'sms', message: '', recipients_type: 'all_contacts', scheduled_date: '' });

  const fetch = useCallback(async () => {
    try {
      const [c, s] = await Promise.all([
        axios.get(`${API}/api/marketing-ext/messaging/campaigns`),
        axios.get(`${API}/api/marketing-ext/messaging/stats`),
      ]);
      // Backend returns array directly, not { campaigns: [...] }
      setCampaigns(Array.isArray(c.data) ? c.data : c.data.campaigns || []); setStats(s.data);
    } catch { /* silent */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const create = async () => {
    if (!form.name.trim() || !form.message.trim()) { toast.error('Name and message required'); return; }
    try { await axios.post(`${API}/api/marketing-ext/messaging/campaigns`, form); toast.success('Created!'); setShowCreate(false); setForm({ name: '', channel: 'sms', message: '', recipients_type: 'all_contacts', scheduled_date: '' }); fetch(); } catch { toast.error('Failed'); }
  };
  const sendNow = async (id) => { try { await axios.post(`${API}/api/marketing-ext/messaging/campaigns/${id}/send`, {}); toast.success('Sent!'); fetch(); } catch { toast.error('Failed'); } };
  const remove = async (id) => { if (!window.confirm('Delete?')) return; try { await axios.delete(`${API}/api/marketing-ext/messaging/campaigns/${id}`); toast.success('Deleted'); fetch(); } catch { toast.error('Failed'); } };

  if (loading) return <div className="flex justify-center p-8"><RefreshCw className="h-5 w-5 animate-spin text-gray-400" /></div>;

  return (
    <div className="space-y-4" data-testid="sms-tab">
      <div className="flex justify-end"><Button onClick={() => setShowCreate(true)} size="sm" data-testid="create-sms-btn"><Plus className="h-4 w-4 mr-1" />New Campaign</Button></div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat icon={MessageSquare} label="Campaigns" value={stats.total_campaigns || 0} color="bg-green-50 text-green-600" />
        <Stat icon={Send} label="Messages Sent" value={(stats.total_sent || 0).toLocaleString()} color="bg-emerald-50 text-emerald-600" />
        <Stat icon={CheckCircle} label="Delivered" value={`${stats.delivery_rate || 0}%`} color="bg-blue-50 text-blue-600" />
        <Stat icon={Zap} label="Response Rate" value={`${stats.response_rate || 0}%`} color="bg-purple-50 text-purple-600" />
      </div>
      <Card className="bg-white border-gray-border"><CardContent className="pt-4">
        {campaigns.length === 0 ? <Empty icon={MessageSquare} text="No SMS/WhatsApp campaigns yet" /> : (
          <div className="space-y-2">{campaigns.map(c => (
            <Row key={c.id} testId={`sms-row-${c.id}`} icon={c.channel === 'whatsapp' ? Phone : MessageSquare} iconColor={c.channel === 'whatsapp' ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'} title={c.name} badges={[{ label: c.status || 'draft', color: c.status === 'sent' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600' }, { label: c.channel, color: c.channel === 'whatsapp' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700' }]} sub={c.message?.substring(0, 60) || ''} right={c.sent_count > 0 ? `${c.sent_count} sent` : ''} actions={<>{c.status !== 'sent' && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => sendNow(c.id)}><Send className="h-3 w-3 mr-0.5" />Send</Button>}<Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => remove(c.id)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button></>} />
          ))}</div>
        )}
      </CardContent></Card>
      <Dialog open={showCreate} onOpenChange={setShowCreate}><DialogContent className="max-w-md"><DialogHeader><DialogTitle>New SMS/WhatsApp Campaign</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Campaign Name *</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="sms-name" /></div>
          <div><Label>Channel</Label><Select value={form.channel} onValueChange={v => setForm(f => ({ ...f, channel: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="sms">SMS</SelectItem><SelectItem value="whatsapp">WhatsApp</SelectItem></SelectContent></Select></div>
          <div><Label>Message *</Label><Textarea value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} rows={3} maxLength={160} /><p className="text-[10px] text-gray-400 mt-0.5">{form.message.length}/160 characters</p></div>
          <div><Label>Recipients</Label><Select value={form.recipients_type} onValueChange={v => setForm(f => ({ ...f, recipients_type: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all_contacts">All Contacts</SelectItem><SelectItem value="leads">Leads Only</SelectItem><SelectItem value="customers">Customers Only</SelectItem></SelectContent></Select></div>
          <div><Label>Schedule Date</Label><Input type="datetime-local" value={form.scheduled_date} onChange={e => setForm(f => ({ ...f, scheduled_date: e.target.value }))} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button><Button onClick={create} data-testid="save-sms-btn">Create</Button></DialogFooter>
      </DialogContent></Dialog>
    </div>
  );
}

// ─── Shared Components ───────────────────────────────────────────────────────

function Empty({ icon: Icon, text }) {
  return <div className="text-center py-12 text-gray-medium"><Icon className="h-10 w-10 mx-auto mb-3 opacity-30" /><p className="text-sm">{text}</p></div>;
}

function Row({ testId, icon: Icon, iconColor, title, badges, sub, right, actions }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border border-gray-border hover:bg-gray-50 transition-colors" data-testid={testId}>
      <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0", iconColor)}><Icon className="h-4 w-4" /></div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm text-text-primary">{title}</span>
          {badges?.map((b, i) => <Badge key={i} className={cn("text-[10px] capitalize", b.color)}>{b.label}</Badge>)}
        </div>
        {sub && <p className="text-xs text-gray-medium truncate">{sub}</p>}
      </div>
      {right && <span className="text-xs font-medium text-gray-medium flex-shrink-0">{right}</span>}
      <div className="flex gap-1 flex-shrink-0">{actions}</div>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function MessagingChannelsPage() {
  return (
    <div className="space-y-6 animate-fadeIn" data-testid="messaging-channels-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary">
          Messaging Channels<PageInfoTooltip pageKey="messaging-channels" />
        </h1>
        <p className="text-gray-medium text-sm mt-1">Manage push notifications, SMS/WhatsApp, and direct mail campaigns from one place</p>
      </div>

      <Tabs defaultValue="push" className="space-y-4">
        <TabsList className="bg-white border border-gray-border w-full sm:w-auto overflow-x-auto flex-nowrap justify-start">
          <TabsTrigger value="push" className="text-xs sm:text-sm whitespace-nowrap" data-testid="tab-push"><Bell className="h-3.5 w-3.5 mr-1.5" />Push</TabsTrigger>
          <TabsTrigger value="sms" className="text-xs sm:text-sm whitespace-nowrap" data-testid="tab-sms"><MessageSquare className="h-3.5 w-3.5 mr-1.5" />SMS & WhatsApp</TabsTrigger>
          <TabsTrigger value="mail" className="text-xs sm:text-sm whitespace-nowrap" data-testid="tab-mail"><Mail className="h-3.5 w-3.5 mr-1.5" />Direct Mail</TabsTrigger>
        </TabsList>
        <TabsContent value="push"><PushTab /></TabsContent>
        <TabsContent value="sms"><SmsWhatsAppTab /></TabsContent>
        <TabsContent value="mail"><DirectMailTab /></TabsContent>
      </Tabs>
    </div>
  );
}
